<template>
    <div class="tooltipElement">
        <template v-if="loading">
            <Loading/>
        </template>
        <button
            class="save_project_btn"
            :class="[getSessionToken.mode === 'merchant' ? 'save-btn-merchant' : '']"
            @click="saveProject"
        >
            {{ $t('editorSceneElementOptionsSaveDesign') }} <span v-if="getSessionToken.mode === 'user'">{{ $t('editorSceneElementOptionsSaveAndAdd') }}</span>
        </button>
        <Tooltip :tooltipText="`${ $t('editorSceneElementOptionsSave') }`" />
        <Modal 
            v-if="showValidationModal"
            @close="showValidationModal = false, placeholdersToFixValidation = []"
        >
            <template v-slot:header>
                <strong>{{ $t('editorSceneElementOptionsValidationErrorMsg') }}</strong>
                <div>
                    <ul>
                        <li
                            v-for="(item, index) in placeholdersToFixValidation"
                            :key="index"
                        >
                            <p>{{ $t('editorSceneElementOptionsValidationValue') }} <strong>{{ item.value }}</strong>, {{ $t('editorSceneElementOptionsValidationError') }} <strong>{{ item.message }}</strong></p>
                        </li>
                    </ul>
                </div>
            </template>
            <template v-slot:body>
                <div class="modalForm">
                    <div class="form-footer">
                        <button @click="showValidationModal = false, placeholdersToFixValidation = []" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                    </div>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import { RepositoryFactory } from '../../../repositories/repositoryFactory';
    import Loading from '@/components/Global/Loading.vue';
    import Tooltip from '@/components/Global/Tooltip.vue';
    import Modal from '@/components/Global/Modal.vue';
    const ProjectRepository = RepositoryFactory.get('projects');
    import { messenger, handlers as messengerHandlers } from '../../../js/messenger';
    import { addFont } from '../../../js/customFonts';

    export default {
        data() {
            return {
                loading: false,
                showValidationModal: false,
                placeholdersToFixValidation: []
            }
        },
        components: {
            Loading,
            Tooltip,
            Modal
        },
        computed: {
            ...mapGetters([
                'getProject',
                'getTemplateFormat',
                'getPageWebSize',
                'getFonts',
                'getSessionToken',
                'getHiddenTabs',
                'getCustomFonts',
                'getPlaceholderUserInputs',
                'getPages',
                'getAllElements'
            ]),
            ...mapState([
                'sessionToken'
            ]),
            saveProjectPayload() {
                const project = JSON.parse(JSON.stringify(this.getProject));

                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.sessionToken.token
                }

                const pages = project.pages.map(page => {
                    // console.log(this.createHtmlFile(page.content, this.getTemplateFormat))
                    return {
                        ...page,
                        html: this.createHtmlFile(page.content, this.getTemplateFormat),
                        content: page.content,
                        thumbnailWidth: this.getPageWebSize.px.width,
                        thumbnailHeight: this.getPageWebSize.px.height
                    }
                })

                let fontCss = '';

                this.getCustomFonts.forEach(font => {
                    const outer = addFont(font.family, font.fontHref, font.weights[0].value, font.weights[0].style).outerHTML;
                    fontCss += outer;
                });

                let placeholderValues = {};

                this.getPlaceholderUserInputs.forEach(placeholder => {
                    let placeholderItem = {};

                    placeholderValues[placeholder['placeholderObj']['placeholderSystemName']] = {
                        value: placeholder['content']
                    }
                });

                const payload = {
                    id: project.id,
                    name: project.name,
                    templateFormatId: project.templateFormatId,
                    pages,
                    setup: {
                        hiddenTabs: this.getHiddenTabs
                    },
                    fontCss,
                    placeholderValues
                };

                return payload;
            },
            placeholdersToValidate() {
                let elementsWithPlaceholder = [];

                this.getPages.forEach(page => {
                    const el = page.content.elements.filter(e => e.placeholder!== undefined).filter(e => e.placeholder.hasPlaceholder === true);
                    elementsWithPlaceholder = [...elementsWithPlaceholder, ...el];
                });

                if (elementsWithPlaceholder.length > 0) {
                    let placeholdersToValidate = {};

                    elementsWithPlaceholder.forEach(el => {
                        let placeholderValues = {};
                        placeholderValues['systemName'] = el.placeholder.placeholderSystemName;
                        placeholderValues['value'] = el.content;
                        placeholdersToValidate[el.placeholder.placeholderSystemName] = placeholderValues;
                    });

                    return placeholdersToValidate;
                }

                return false;
            }
        },
        methods: {
            createHtmlFile(pageContent, format) {
                const doc = document.implementation.createHTMLDocument("New Document");
                const head = doc.head;
                const style = doc.createElement('style');
                head.appendChild(style);
                style.type = 'text/css';
                const css = `
                    body {
                        margin: 0;
                        -webkit-print-color-adjust: exact !important;
                    }
                    .textCapitals {
                        text-transform: uppercase;
                    }
                    .textUnderline {
                        text-decoration: underline;
                    }
                    @page {
                        size: ${format.pageWidth + 2 * format.pageBleed}mm ${format.pageHeight + 2 * format.pageBleed}mm;
                        margin: 0;
                    }
                    @media print {
                        html, body {
                            width: ${format.pageWidth + 2 * format.pageBleed}mm;
                            height: ${format.pageHeight + 2 * format.pageBleed}mm;
                        }
                        .printPage {
                            overflow: hidden;
                        }
                    }
                `
                style.appendChild(doc.createTextNode(css))


                const printPage = doc.createElement('div');
                printPage.style.width = `${format.pageWidth + 2 * format.pageBleed}mm`;
                printPage.style.height = `${format.pageHeight + 2 * format.pageBleed}mm`;
                printPage.style.position = `relative`;
                printPage.style.backgroundColor = `#${pageContent.pageColor.hex}`;
                printPage.style.borderRadius = `${pageContent.pageRadius}%`;
                printPage.style.backgroundImage = `url(${pageContent.backgroundImage.url})`;
                printPage.style.backgroundSize = pageContent.backgroundImage.backgroundSize;
                printPage.style.backgroundPosition = pageContent.backgroundImage.backgroundPosition;
                printPage.style.backgroundRepeat = pageContent.backgroundImage.backgroundRepeat;
                printPage.classList.add('printPage');

                let allUsedFonts = [];

                this.$store.dispatch('UPDATE_ALL_ELEMENTS', {
                    newTransforms: true
                });

                pageContent.elements.forEach(el => {
                    
                    let element = document.getElementById(el.id).cloneNode(true);
                    
                    element.style.transform = `translate(${el.webSizeMM.translate[0] * this.getPageWebSize.ratio}mm, ${el.webSizeMM.translate[1] * this.getPageWebSize.ratio}mm) scale(${el.frame.scale[0]}, ${el.frame.scale[1]})` + `rotate(${el.frame.rotate}deg)`;
                    element.style.width = `${el.webSizeMM.width * this.getPageWebSize.ratio}mm`;
                    element.style.height = `${el.webSizeMM.height * this.getPageWebSize.ratio}mm`;

                    // element.style.transform = `translate(${el.webSizeMM.translate[0]}mm, ${el.webSizeMM.translate[1]}mm) scale(${el.frame.scale[0]}, ${el.frame.scale[1]})` + `rotate(${el.frame.rotate}deg)`;
                    // element.style.width = `${el.webSizeMM.width}mm`;
                    // element.style.height = `${el.webSizeMM.height}mm`;

                    if (el.type === 'PHOTO_CELL_ELEMENT') {
                        const imageContainer = document.getElementById(`editorPhotoCellElement-${el.id}`).cloneNode(true);

                        imageContainer.style.width = `100%`;
                        imageContainer.style.height = `100%`;
                        imageContainer.style.position = `relative`;

                        

                        if (el.maskImg.src !== null && !el.maskImg.isFromSvg) {
                            const div = document.createElement('div');
                            div.style.transform = `translate(${el.imageWebSizeMM.translate[0] * this.getPageWebSize.ratio}mm, ${el.imageWebSizeMM.translate[1] * this.getPageWebSize.ratio}mm) scale(${el.imageFrame.scale[0]}, ${el.imageFrame.scale[1]})` + `rotate(${el.imageFrame.rotate}deg)`;
                            div.style.filter = imageContainer.getElementsByClassName('photoCellImg')[0].getElementsByTagName('img')[0].style.filter;
                            div.style.width = `${el.maskImg.width * 0.084667 * this.getPageWebSize.ratio}mm`;
                            div.style.height = `${el.maskImg.height * 0.084667 * this.getPageWebSize.ratio}mm`;
                            const image = document.createElement('img');
                            image.style.width = '100%';
                            image.style.height = '100%';
                            image.src = imageContainer.getElementsByClassName('photoCellImg')[0].getElementsByTagName('img')[0].src;
                            div.appendChild(image);
                            imageContainer.innerHTML = "";
                            imageContainer.appendChild(div);
                        } else if (el.maskImg.src !== null && el.maskImg.isFromSvg) {
                            const div = document.createElement('div');
                            div.style.transform = `translate(${el.imageWebSizeMM.translate[0] * this.getPageWebSize.ratio}mm, ${el.imageWebSizeMM.translate[1] * this.getPageWebSize.ratio}mm) scale(${el.imageFrame.scale[0]}, ${el.imageFrame.scale[1]})` + `rotate(${el.imageFrame.rotate}deg)`;
                            div.style.width = `${el.maskImg.width * 0.084667 * this.getPageWebSize.ratio}mm`;
                            div.style.height = `${el.maskImg.height * 0.084667 * this.getPageWebSize.ratio}mm`;
                            const svg = imageContainer.getElementsByTagName('svg')[0];
                            svg.setAttribute('width', '100%');
                            svg.setAttribute('height', '100%');
                            svg.style.display = `block`;
                            div.appendChild(svg);
                            imageContainer.innerHTML = "";
                            imageContainer.appendChild(div);
                        } else {
                            imageContainer.innerHTML = "";
                        }
                        
                        
                        element.innerHTML = ""
                        element.appendChild(imageContainer)
                    }
                    if (el.type === 'TEXT_ELEMENT') {
                        allUsedFonts.push(el.font.family)
                        const textElement = element.getElementsByTagName('div')[0];
                        
                        // textElement.style.fontSize = `${el.font.size * 1.32815 * 0.084667 * this.getPageWebSize.ratio}mm`;
                        textElement.style.fontSize = `${el.font.size}pt`;                    
                        
                        textElement.style.height = '100%';
                        textElement.style.overflow = 'hidden';
                        textElement.style.boxSizing = 'border-box';

                        textElement.innerHTML = el.content;
                        
                        element.innerHTML = "";
                        element.appendChild(textElement);
                    }
                    if (el.type === 'QR_CODE_ELEMENT') {
                        element.style.width = `${parseInt(el.width) * 25.4 / 300 * this.getPageWebSize.ratio}mm`;
                        element.style.height = `${parseInt(el.height) * 25.4 / 300 * this.getPageWebSize.ratio}mm`;

                        const qrContainer = document.getElementById(`editorQrCodeElement-${el.id}`).cloneNode(true);
                        qrContainer.style.position = 'relative';
                        const qrSvg = qrContainer.getElementsByTagName('svg')[0];

                        const svgSize =  qrSvg.getAttribute('width');
                        qrSvg.setAttribute('viewbox', `0 0 ${svgSize} ${svgSize}`);
                        qrSvg.setAttribute('style', 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;');

                        qrContainer.innerHTML = "";
                        qrContainer.appendChild(qrSvg);

                        element.innerHTML = "";
                        element.appendChild(qrContainer)                        
                    }

                    printPage.appendChild(element)
                })

                const usedFonts = [...(new Set(allUsedFonts))]

                const links = usedFonts.map(usedFont => {
                    const font = this.getFonts.find(f => f.family === usedFont); 

                    if (font !== undefined) {
                        return font.fontHref
                    }

                    
                })

                links.forEach(link => {
                    const l = doc.createElement('link');
                    head.appendChild(l)
                    l.href = link;
                    l.rel = "stylesheet";
                    
                })
                doc.body.appendChild(printPage)
                
                // return doc.documentElement.innerHTML
                return printPage.outerHTML;
            },

            saveProject() {
                this.loading = true;
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.sessionToken.token
                }

                const that = this;

                if (this.placeholdersToValidate) {
                    messenger.validatePlaceholderForm(this.placeholdersToValidate)
                    .then(function (result) {
                        Object.keys(result).forEach(item => {
                            that.setContentFromValidation(result[item].systemName, result[item].value)
                        });
                        const invalidFields = Object.keys(result).filter(key => result[key].isValid === false);
                        if (invalidFields.length > 0) {
                            that.loading = false;
                            invalidFields.forEach(field => {
                                that.placeholdersToFixValidation.push({ value: result[field].value, message: result[field].message })
                            });
                            that.showValidationModal = true;

                        } else {
                            ProjectRepository.saveProject(that.saveProjectPayload, headers)
                            .then(response => {
                                console.log(response)
                                messenger.sendFinishEditorMessage(that.saveProjectPayload.id)
                            })
                            .catch(error => {
                                console.log(error)
                            })
                            .finally(() => {
                                that.loading = false;
                            });
                        }

                        console.log('Form Success!', result);
                    }, function (error) {
                        console.log('Form Error!', error);
                    });
                } else {
                    ProjectRepository.saveProject(this.saveProjectPayload, headers)
                    .then(response => {
                        messenger.sendFinishEditorMessage(this.saveProjectPayload.id)
                    })
                    .catch(error => {
                        console.log(error)
                    })
                    .finally(() => {
                        this.loading = false;
                    });
                }
            },
            setContentFromValidation(placeholderSystemName, content) {
                const elementsWithPlaceholder = this.getAllElements.filter(el => Object.keys(el).includes('placeholder')).filter(el => el.placeholder.hasPlaceholder).filter(el => el.placeholder.placeholderSystemName === placeholderSystemName);
                
                elementsWithPlaceholder.forEach(el => {
                    let textPayload = {
                        id: el.id,
                        content,
                        contentFromPlaceholder: true
                    }
                    this.$store.dispatch('CHANGE_TEXT', textPayload);
                });
            }
        }
    }
</script>

<style lang="scss" scoped>
    .save_project_btn {
        background-color: var(--primary-color);
        border: 1px solid var(--primary-color);
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-content: center;
        justify-content: center;
        color: #fff;
        padding: 15px 0;
        width: calc(100% - 30px);
        margin: 30px auto;
        font-weight: 600;
        font-size: 13px;
        line-height: 17px;
    }
    .save_project_btn:hover {
        opacity: 0.8;
    }
    .save-btn-merchant {
        display: inline-block;
        margin: 0;
        padding: 6px 15px;
        width: auto;
    }
    @media (max-width: 1200px) {
        .save_project_btn {
            flex-direction: column;
            align-items: center;
            font-size: 11px;
            padding: 5px 0;
        }
        .save-btn-merchant {
            padding: 4px 5px;
            font-size: 10px;
        }
    }
</style>